<template>
  <ak-sidebar>
    <ak-loading
      v-model:active="isLoading"
      :can-cancel="false"
      :is-full-page="false"
      :backdrop="false"
    />

    <template v-if="!isLoading && folders.length">
      <ak-box class="pr-small h-full is-scrollable">
        <slot name="beforeFolders" />

        <ak-dropdown
          v-if="showSort"
          class="pull-right"
          icon="funnel-b"
          position-x="right"
        >
          <template #trigger>
            <ak-button size="small" color="transparent" circle>
              <ak-icon type="funnel-b" size="small" />
            </ak-button>
          </template>
          <template #default>
            <ak-dropdown-item custom>
              <ak-field
                label="Sort By"
                custom-class="color-grey"
                margin-bottom="mb-none"
              >
                <ak-radio-group
                  v-model="defaultSortOrder"
                  name="sort"
                  :options="sortOptions"
                  @change="changeSortOrder"
                />
              </ak-field>
            </ak-dropdown-item>
          </template>
        </ak-dropdown>

        <h4 class="color-grey">Folders ({{ folders.length || '0' }})</h4>
        <div class="relative">
          <div class="folder-list list-bare">
            <div
              class="folder-list__item cursor-pointer"
              :class="{ 'is-active': activeFolder === 'all' }"
              @click.prevent.stop="selectFolder('all', $event)"
            >
              <ak-icon
                :type="activeFolder === 'all' ? 'folder-open' : 'folder'"
                class="folder-list__item-indicator mr-small color-grey"
              ></ak-icon>
              <div class="folder-list__item-body">All</div>
            </div>
            <div
              v-for="(folder, index) of sortedFolders"
              :key="index"
              class="folder-list__item cursor-pointer"
              :class="{ 'is-active': activeFolder === folder }"
              @click.prevent.stop="selectFolder(folder, $event)"
            >
              <ak-icon
                :type="activeFolder === folder ? 'folder-open' : 'folder'"
                class="folder-list__item-indicator mr-small color-grey"
              ></ak-icon>
              <div class="folder-list__item-body">
                {{ folder }}
              </div>
              <div class="folder-list__item-action">
                <ak-dropdown position-x="right">
                  <template #trigger>
                    <ak-button size="small" color="transparent" circle>
                      <ak-icon type="more-vertical" />
                    </ak-button>
                  </template>
                  <template #default>
                    <ak-dropdown-item @click="deleteFolder(folder, $event)">
                      <a>Delete</a>
                    </ak-dropdown-item>
                  </template>
                </ak-dropdown>
              </div>
            </div>
            <ak-button
              size="tiny"
              ghost
              @click.prevent.stop="modalActive = !modalActive"
            >
              Create Folder
            </ak-button>
          </div>
        </div>
        <slot name="afterFolders" />
      </ak-box>
    </template>

    <template v-if="!isLoading && !folders.length">
      <ak-empty-state class="pt" icon="folder" title="No folders found">
        <ak-button
          size="small"
          @click.prevent.stop="modalActive = !modalActive"
        >
          Create Folder
        </ak-button>
      </ak-empty-state>
    </template>

    <ak-create-folder-modal
      v-model:is-active="modalActive"
      :folders-list="folders"
      :folder-created="folderCreated"
    />
  </ak-sidebar>
</template>

<script>
import { computed, ref, watch, onMounted, defineComponent } from 'vue'
import CreateFolderModal from './CreateFolderModal.vue'
import Sidebar from '@akerolabs/akero-vue/src/components/sidebar/Sidebar.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import RadioGroup from '@akerolabs/akero-vue/src/components/radioGroup/RadioGroup.vue'
import Dropdown from '@akerolabs/akero-vue/src/components/dropdown/Dropdown.vue'
import DropdownItem from '@akerolabs/akero-vue/src/components/dropdown/DropdownItem.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import EmptyState from '@akerolabs/akero-vue/src/components/emptyState/EmptyState.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { sleep } from '@akerolabs/akero-vue/src/utils/helpers'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-folders-sidebar></ak-folders-sidebar>
   */
  name: 'AkFoldersSidebar',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [CreateFolderModal.name]: CreateFolderModal,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Sidebar.name]: Sidebar,
    [Button.name]: Button,
    [Box.name]: Box,
    [Icon.name]: Icon,
    [Field.name]: Field,
    [RadioGroup.name]: RadioGroup,
    [Loading.name]: Loading,
    [EmptyState.name]: EmptyState,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },

    /**
     * @type {String}
     * @description This is the current active folder
     */
    activeFolder: {
      default: 'All',
      type: String,
    },

    /**
     * @type {Boolean}
     * @description Determines whether or not to show the sort
     */
    showSort: {
      default: true,
      type: Boolean,
    },
  },

  emits: ['update:activeFolder'],

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props, { emit }) {
    const newActiveFolder = ref(props.activeFolder)
    const modalActive = ref(false)
    const defaultSortOrder = ref('desc')
    const sortedFolders = ref([])
    const folders = ref([])
    const isLoading = ref(true)
    const sortOptions = ref([
      { label: 'Chronological', value: 'desc' },
      { label: 'Alphabetical', value: 'asc' },
    ])

    /**
     * @returns {Object}
     * @description Returns the query object for making a request to
     * fetch the account folders.
     */
    const foldersRequest = computed(() => {
      return {
        params: {
          account: props.activeAccount._id,
          agency: props.activeAgency._id,
          overrideAccess: true,
          filters: 'settings.workflow.folders',
        },
      }
    })

    /**
     * @returns {Promise}
     * @description Returns a promise to fetch the folders from the current
     * account.
     */
    const fetchFolders = () => {
      return request.get(
        `/accounts/${props.activeAccount._id}`,
        foldersRequest.value,
      )
    }

    /**
     * @returns {Promise}
     * @description Saves the folders in the users account
     */
    const saveFolders = () => {
      const data = folders.value.reduce((acc, val) => {
        if (val && val !== '') acc.push(val)
        return acc
      }, [])

      return request.put(
        `/accounts/${props.activeAccount._id}/setting/folders`,
        { 'settings.workflow.folders': data },
      )
    }

    /**
     * @param {String} folderName
     * @description This sets the current active folder
     */
    const selectFolder = (folderName = 'all', event) => {
      // prevents the select folder event firing, for some reason in the
      // deleteFolder method $event is not defined and the .stop.prevent
      // click modifiers also do not trigger. This causes this method to
      // be called afterwards, so we will check the parentNode before
      // we update the selected folder.
      if (event.target && event.target.parentNode) {
        if (
          !event.target.parentNode.classList.contains('folder-list') &&
          !event.target.parentNode.classList.contains('folder-list__item')
        ) {
          return
        }
      }
      newActiveFolder.value = folderName
    }

    /**
     * @param {String} folderName
     * @description Method to create the folder
     */
    const folderCreated = async (folderName) => {
      folders.value.push(folderName)
      await sortFolders(folders.value)
      await saveFolders()
      modalActive.value = false
    }

    /**
     * @param {String} folderName
     * @description Removed a folder from the array
     */
    const removeFolder = async (folderName) => {
      const idx = folders.value.findIndex((i) => i == folderName)
      if (idx >= 0) {
        folders.value.splice(idx, 1)
        await sortFolders(folders.value)
        await saveFolders()
        ToastProgrammatic.open({
          message: 'Folder has been deleted.',
          type: 'success',
        })
      }
    }

    /**
     * @param {String} folderName
     * @description Triggers a dialog to confirm if you want to
     * delete the requested folder.
     */
    const deleteFolder = (folderName) => {
      DialogProgrammatic.prompt({
        title: 'Deleting folder',
        message: `Please type "${folderName}" to confirm`,
        inputAttrs: {
          pattern: folderName,
        },
        trapFocus: true,
        type: 'danger',
        hasIcon: true,
        confirmText: 'Delete Folder',
        onConfirm: async () => await removeFolder(folderName),
      })
    }

    const sortFolders = async (folders) => {
      let copy = [...folders]
      if (defaultSortOrder.value === 'asc') {
        copy = copy.sort()
      }
      sortedFolders.value = copy
    }

    const changeSortOrder = async () => {
      isLoading.value = true
      await sleep(500)
      await sortFolders(folders.value)
      isLoading.value = false
    }

    onMounted(async () => {
      try {
        const response = await fetchFolders()
        folders.value = response.data.data.settings.workflow.folders
        await sortFolders(folders.value)
      } catch (ex) {
        ToastProgrammatic.open({
          message: 'Error failed to fetch workflow folders.',
          type: 'danger',
        })
      }
      isLoading.value = false
    })

    watch(newActiveFolder, (value) => {
      emit('update:activeFolder', value)
    })

    return {
      newActiveFolder,
      modalActive,
      defaultSortOrder,
      sortedFolders,
      folders,
      isLoading,
      sortOptions,
      foldersRequest,
      fetchFolders,
      saveFolders,
      selectFolder,
      folderCreated,
      removeFolder,
      deleteFolder,
      sortFolders,
      changeSortOrder,
    }
  },
})
</script>
